import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { SparkoLogo } from "../../components/SparkoLogo";
import { Icon } from "../../components/common";

export default function AdminLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpMsg, setFpMsg] = useState("");
  const [fpLoading, setFpLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("sparko_token")) {
      api.get("/auth/me").then(() => nav("/admin")).catch(() => localStorage.removeItem("sparko_token"));
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sparko_token", data.token);
      nav("/admin");
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setFpMsg(""); setFpLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fpEmail });
      setFpMsg(data.message || "If that email is registered, a password reset link has been sent.");
    } catch (e) {
      setFpMsg("If that email is registered, a password reset link has been sent.");
    }
    setFpLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy">
      <div className="relative hidden lg:block grain overflow-hidden">
        <img src="https://images.unsplash.com/photo-1718152421680-d1580e843cc9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy/70 to-navy" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <SparkoLogo dark={false} size={34} tagline />
          <div>
            <h2 className="font-display text-4xl text-white leading-tight">Sparko Admin</h2>
            <p className="mt-3 text-white/60 max-w-sm">Manage services, content, submissions and more from one premium dashboard.</p>
          </div>
        </div>
      </div>

      <div className="grid place-items-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center"><SparkoLogo dark={false} size={30} tagline /></div>
          {mode === "login" ? (
            <>
              <h1 className="font-display text-3xl text-white">Welcome back</h1>
              <p className="mt-2 text-white/50">Sign in to your admin dashboard.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
                <div>
                  <label className="text-sm text-white/70">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                    className="mt-1 w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-white placeholder-white/30 outline-none focus:border-blue" placeholder="admin@sparko.nl" />
                </div>
                <div>
                  <label className="text-sm text-white/70">Password</label>
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                    className="mt-1 w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-white placeholder-white/30 outline-none focus:border-blue" placeholder="••••••••" />
                </div>
                {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
                <button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue px-6 py-3.5 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
                  {loading ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="LogIn" size={18} />} Sign In
                </button>
                <button type="button" onClick={() => { setMode("forgot"); setErr(""); setFpEmail(email); setFpMsg(""); }} data-testid="forgot-password-link"
                  className="w-full text-sm text-white/50 hover:text-white transition-colors">Forgot password?</button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl text-white">Reset password</h1>
              <p className="mt-2 text-white/50">Enter your admin email and we&apos;ll send you a secure reset link.</p>
              <form onSubmit={forgotSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
                <div>
                  <label className="text-sm text-white/70">Email</label>
                  <input type="email" required value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} data-testid="forgot-email"
                    className="mt-1 w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-white placeholder-white/30 outline-none focus:border-blue" placeholder="admin@sparko.nl" />
                </div>
                {fpMsg && <p className="text-sm text-blue" data-testid="forgot-message">{fpMsg}</p>}
                <button type="submit" disabled={fpLoading} data-testid="forgot-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue px-6 py-3.5 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
                  {fpLoading ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Mail" size={18} />} Send reset link
                </button>
                <button type="button" onClick={() => { setMode("login"); setFpMsg(""); }} data-testid="back-to-login"
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors"><Icon name="ArrowLeft" size={14} /> Back to sign in</button>
              </form>
            </>
          )}
          <a href="/" className="mt-6 inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white"><Icon name="ArrowLeft" size={14} /> Back to website</a>
        </motion.div>
      </div>
    </div>
  );
}
